import React, { useContext, useMemo } from 'react';
import {
  DayPicker,
  MonthPicker,
  YearPicker,
} from 'ncoded-component-library/build/components/organisms/Datepicker/components';
import { PickersType } from 'ncoded-component-library/build/components/organisms/Datepicker/picker';
import DatepickerContext from 'ncoded-component-library/build/components/organisms/Datepicker/provider/Datepicker.context';
import {
  LeftArrow,
  RightArrow,
} from 'ncoded-component-library/build/components/organisms/Datepicker/svgs';
import {
  Pickers,
  YEARS_ON_PAGE,
} from 'ncoded-component-library/build/components/organisms/Datepicker/utils';
import { useTranslation } from 'react-i18next';
import { formatMonthYear } from '../../utils';

const PICKERS: Record<PickersType, React.FC> = {
  [Pickers.DAY_PICKER]: DayPicker,
  [Pickers.MONTH_PICKER]: MonthPicker,
  [Pickers.YEAR_PICKER]: YearPicker,
};

/**
 * Stands in for the library's own `Datepicker` body, which we can't configure:
 * its day header prints the month as a number ("12 2026"). The provider, the
 * three grids and the context are all exported, so only the header and the
 * shell around it have to be ours — the grids stay the library's, and the class
 * names stay the same so the field's styles keep applying.
 */
const DatepickerCalendar: React.FC = () => {
  const {
    showInMenu,
    setShowInMenu,
    currentDate,
    setCurrentDate,
    yearFrom,
    setYearFrom,
    date,
    name,
  } = useContext(DatepickerContext);

  const { t } = useTranslation();

  // Each view steps by its own unit and drills down to the next one, matching
  // the three headers the library ships.
  const { label, goBack, goForward, drillDown } = useMemo(() => {
    switch (showInMenu) {
      case Pickers.MONTH_PICKER:
        return {
          label: `${currentDate.getFullYear()}`,
          goBack: () =>
            setCurrentDate(
              (old) =>
                new Date(old.getFullYear() - 1, old.getMonth(), old.getDate()),
            ),
          goForward: () =>
            setCurrentDate(
              (old) =>
                new Date(old.getFullYear() + 1, old.getMonth(), old.getDate()),
            ),
          drillDown: () => setShowInMenu(Pickers.DAY_PICKER),
        };
      case Pickers.YEAR_PICKER:
        return {
          label: `${yearFrom} - ${yearFrom + YEARS_ON_PAGE - 1}`,
          goBack: () => setYearFrom((old) => old - YEARS_ON_PAGE),
          goForward: () => setYearFrom((old) => old + YEARS_ON_PAGE),
          drillDown: () => setShowInMenu(Pickers.MONTH_PICKER),
        };
      default:
        return {
          label: formatMonthYear(currentDate),
          goBack: () =>
            setCurrentDate(
              (old) =>
                new Date(old.getFullYear(), old.getMonth() - 1, old.getDate()),
            ),
          goForward: () =>
            setCurrentDate(
              (old) =>
                new Date(old.getFullYear(), old.getMonth() + 1, old.getDate()),
            ),
          drillDown: () => setShowInMenu(Pickers.YEAR_PICKER),
        };
    }
  }, [showInMenu, currentDate, yearFrom, setCurrentDate, setShowInMenu, setYearFrom]);

  const Picker = PICKERS[showInMenu];

  return (
    <div className="ncoded-datepicker">
      <header>
        <div className="datepicker-header">
          <button
            type="button"
            className="datepicker-header__nav"
            onClick={goBack}
            aria-label={t('prev')}
          >
            <LeftArrow />
          </button>

          <button
            type="button"
            className="datepicker-header__heading"
            onClick={drillDown}
          >
            <p>{label}</p>
          </button>

          <button
            type="button"
            className="datepicker-header__nav"
            onClick={goForward}
            aria-label={t('next')}
          >
            <RightArrow />
          </button>
        </div>
      </header>

      <Picker />

      <input name={name} type="hidden" value={+date} readOnly />
    </div>
  );
};

export default DatepickerCalendar;
